import {
  LayoutDashboard, Building2, Package, Users, Receipt, Stethoscope,
  Tag, Settings, ShieldCheck, Briefcase,
  CalendarCheck2, BarChart3, SearchCheck,
  MapPinned, TrendingUp, Activity, Copy, AlertTriangle, FileWarning, FileText,
  DatabaseBackup,
  KanbanSquare, MessageSquare,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Authorities (Spatie wildcard) que liberam o item. Se vazio = sempre visível. */
  authorities?: string[]
  /** Tipos de usuário que veem o item (ex.: ['AFILIADO']). Sem `types` = todos da seção. */
  types?: string[]
  /** NavLink `end` — casa só a rota exata (evita marcar ativo em sub-rotas, ex.: /revenues vs /revenues/projection). */
  end?: boolean
  /** Subitens — exibidos colapsáveis sob o item pai. Quando há children, o `to`
   *  do pai é usado como rota raiz e também conta para marcar "ativo" se
   *  qualquer child estiver na URL ativa. */
  children?: NavItem[]
}

export interface NavSection {
  title: string
  items: NavItem[]
  /**
   * Quando definido, a seção só aparece para usuários destes tipos
   * (ex.: ['EMPRESA'] para o painel da empresa). Sem `types`, é o painel
   * administrativo padrão — escondido para EMPRESA.
   */
  types?: string[]
}

/** Tipos do painel de parceiros (rede de vendas hierárquica). */
const PARTNER_TYPES: string[] = ['DIRETOR', 'SUPERVISOR', 'AFILIADO', 'VENDEDOR']

export const NAV: NavSection[] = [
  {
    title: 'Geral',
    items: [
      { to: '/',          label: 'Dashboard',  icon: LayoutDashboard, authorities: ['dashboard.view'] },
    ],
  },
  {
    title: 'Comercial',
    items: [
      { to: '/commercial/subscribers',       label: 'Assinaturas',         icon: Users,     authorities: ['commercial.subscribers.view'] },
      { to: '/commercial/companies',         label: 'Empresas',            icon: Building2, authorities: ['commercial.companies.view'] },
      { to: '/plans',                        label: 'Planos',              icon: Package,   authorities: ['commercial.plans.view'] },
      { to: '/commercial/discount-coupons',  label: 'Cupons de desconto',  icon: Tag,       authorities: ['commercial.discount_coupons.view'] },
      { to: '/commercial/proposals',         label: 'Propostas comerciais', icon: FileText,  authorities: ['commercial.proposals.view'] },
      { to: '/commercial/proposals/kanban',  label: 'Kanban de propostas',  icon: KanbanSquare, authorities: ['commercial.proposals.view'] },
      {
        to: '/commercial/reports',
        label: 'Relatórios',
        icon: BarChart3,
        // O pai aparece se QUALQUER relatório do grupo estiver liberado.
        authorities: ['commercial.reports.general', 'commercial.reports.providers', 'commercial.reports.age', 'commercial.reports.location'],
        children: [
          { to: '/commercial/reports/general',   label: 'Gerais',                   icon: BarChart3, authorities: ['commercial.reports.general'] },
          { to: '/commercial/reports/providers', label: 'Provedores',               icon: BarChart3, authorities: ['commercial.reports.providers'] },
          { to: '/commercial/reports/age',       label: 'Maioridade',               icon: BarChart3, authorities: ['commercial.reports.age'] },
          { to: '/commercial/reports/location',  label: 'Assinantes por localidade', icon: BarChart3, authorities: ['commercial.reports.location'] },
        ],
      },
    ],
  },
  {
    title: 'Financeiro',
    items: [
      { to: '/revenues',            label: 'Faturas',              icon: Receipt, end: true, authorities: ['financial.revenues.view'] },
      { to: '/revenues/projection', label: 'Projeção próximo mês', icon: TrendingUp,         authorities: ['financial.projection.view'] },
      { to: '/financial/duplicate-invoices', label: 'Faturas duplicadas', icon: Copy,        authorities: ['financial.duplicate_invoices.view'] },
      {
        to: '/financial/reports',
        label: 'Relatórios',
        icon: BarChart3,
        authorities: ['financial.reports.earnings', 'financial.reports.commissions'],
        children: [
          { to: '/financial/reports/earnings',    label: 'Ganhos',          icon: BarChart3, authorities: ['financial.reports.earnings'] },
          { to: '/financial/reports/commissions', label: 'Comissionamento', icon: Receipt,   authorities: ['financial.reports.commissions'] },
        ],
      },
    ],
  },
  {
    title: 'Atendimentos',
    items: [
      { to: '/medical-appointments', label: 'Agendamentos', icon: CalendarCheck2, authorities: ['medical_appointments.appointments.view'] },
      { to: '/support/inbox',        label: 'Central de atendimento', icon: MessageSquare, authorities: ['support.chat.view'] },
      { to: '/support/settings',     label: 'Configurações do atendimento', icon: Settings, authorities: ['support.teams.view', 'support.labels.view', 'support.canned.view', 'support.inboxes.view'] },
    ],
  },
  {
    title: 'Credenciamento',
    items: [
      { to: '/credentialing/providers',            label: 'Credenciados',           icon: Stethoscope, authorities: ['credentialing.providers.view'] },
      { to: '/credentialing/kanban',               label: 'Kanban',                 icon: Briefcase,   authorities: ['credentialing.providers.view'] },
      { to: '/credentialing/specialty-search',     label: 'Filtro de especialidades', icon: SearchCheck, authorities: ['credentialing.providers.specialty_search'] },
      {
        to: '/credentialing/reports',
        label: 'Relatórios',
        icon: BarChart3,
        authorities: ['credentialing.reports.dashboard', 'credentialing.reports.commissions', 'credentialing.reports.map'],
        children: [
          { to: '/credentialing/reports/dashboard',    label: 'Dashboard',            icon: BarChart3, authorities: ['credentialing.reports.dashboard'] },
          { to: '/credentialing/reports/commissions',  label: 'Comissões',            icon: Receipt,   authorities: ['credentialing.reports.commissions'] },
          { to: '/credentialing/reports/map',          label: 'Mapa de credenciados', icon: MapPinned, authorities: ['credentialing.reports.map'] },
        ],
      },
    ],
  },
  {
    title: 'Links',
    items: [
      { to: '/catalog', label: 'Catálogo', icon: Package, authorities: ['links.catalog.view'] },
    ],
  },
  {
    title: 'Administração',
    items: [
      { to: '/administractive/hierarchies', label: 'Hierarquias', icon: ShieldCheck, authorities: ['administractive.hierarchies.view'] },
      { to: '/administractive/users',       label: 'Usuários',    icon: Users,       authorities: ['administractive.users.view'] },
      { to: '/settings',                    label: 'Configurações', icon: Settings,  authorities: ['settings.*'] },
    ],
  },
  {
    title: 'Integrações',
    items: [
      { to: '/integrations/medicar',            label: 'Medicar',            icon: Activity, authorities: ['integrations.medicar.view'] },
      { to: '/integrations/backups',            label: 'Backups',            icon: DatabaseBackup, authorities: ['integrations.backups.view'] },
      {
        to: '/integrations/asaas', label: 'Asaas', icon: Receipt,
        authorities: ['integrations.asaas.ungenerated_view', 'integrations.asaas.charge_finder', 'integrations.error_logs.view'],
        children: [
          { to: '/integrations/asaas/ungenerated',   label: 'Faturas não geradas',  icon: FileWarning,   authorities: ['integrations.asaas.ungenerated_view'] },
          { to: '/integrations/asaas/charge-finder', label: 'Buscador de cobrança', icon: SearchCheck,   authorities: ['integrations.asaas.charge_finder'] },
          { to: '/integrations/error-logs',          label: 'Log de erros',         icon: AlertTriangle, authorities: ['integrations.error_logs.view'] },
        ],
      },
    ],
  },

  // ===== Painel da EMPRESA (impersonação ou login EMPRESA) =====
  {
    title: 'Geral',
    types: ['EMPRESA'],
    items: [
      { to: '/my-company/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'Financeiro',
    types: ['EMPRESA'],
    items: [
      { to: '/my-company/invoices', label: 'Faturas', icon: Receipt },
    ],
  },
  {
    title: 'Minha empresa',
    types: ['EMPRESA'],
    items: [
      { to: '/my-company/collaborators', label: 'Colaboradores', icon: Users },
      { to: '/my-company/branches',      label: 'Filiais',       icon: Building2 },
      { to: '/my-company/departments',   label: 'Departamentos', icon: Briefcase },
    ],
  },
  {
    title: 'Links',
    types: ['EMPRESA'],
    items: [
      { to: '/my-company/catalog', label: 'Catálogo', icon: Package },
    ],
  },

  // ===== Painel PARCEIRO (Diretor / Supervisor / Afiliado / Vendedor) =====
  {
    title: 'Geral',
    types: PARTNER_TYPES,
    items: [
      { to: '/partner/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'Financeiro',
    types: PARTNER_TYPES,
    items: [
      { to: '/partner/invoices', label: 'Faturas', icon: Receipt },
      {
        to: '/partner/reports',
        label: 'Relatórios',
        icon: BarChart3,
        children: [
          { to: '/partner/reports/earnings',    label: 'Ganhos',    icon: BarChart3 },
          { to: '/partner/reports/commissions', label: 'Comissões', icon: Receipt },
        ],
      },
    ],
  },
  {
    title: 'Comercial',
    types: PARTNER_TYPES,
    items: [
      // Diretor, supervisor e afiliado gerem seus próprios modelos de comissão
      // (cada um vê só os seus) para aplicar na rede direta.
      { to: '/partner/commission-models', label: 'Modelos de comissão', icon: Tag, types: ['DIRETOR', 'SUPERVISOR', 'AFILIADO'] },
    ],
  },
  {
    title: 'Rede',
    types: PARTNER_TYPES,
    items: [
      // Vendedor não cadastra ninguém.
      { to: '/partner/network', label: 'Minha rede', icon: Users, types: ['DIRETOR', 'SUPERVISOR', 'AFILIADO'] },
    ],
  },
  {
    title: 'Links',
    types: PARTNER_TYPES,
    items: [
      { to: '/partner/catalog', label: 'Catálogo', icon: Package },
      { to: '/partner/sales-link', label: 'Vendas', icon: Briefcase, types: ['AFILIADO', 'VENDEDOR'] },
    ],
  },
]

/** Avalia se um item está visível para uma lista de authorities do user. */
export function isNavVisible(item: NavItem, userAuths: string[]): boolean {
  if (!item.authorities || item.authorities.length === 0) return true
  return item.authorities.some((needed) => hasAuthority(userAuths, needed))
}

/**
 * Uma permissão específica está coberta pelas authorities do usuário?
 * Mesma semântica do backend (CheckAuthority), reaproveitada fora do menu para
 * gatear botões e ações — ver `useAuthority`.
 */
export function hasAuthority(userAuths: string[], needed: string): boolean {
  // `*` é o curinga do ADMINISTRADOR. `application.*` não entra mais aqui: ele
  // é o gate COARSE do painel no backend, e tratá-lo como passe livre fazia uma
  // permissão granular abrir o menu inteiro.
  if (userAuths.includes('*')) return true
  return userAuths.some((u) => matchWildcard(u, needed))
}

/** Curingas implícitos por TIPO (parceiro/empresa) — não são permissões de painel. */
const TYPE_WILDCARDS = ['director.*', 'supervisor.*', 'affiliate.*', 'seller.*', 'company.*']

function matchWildcard(userAuth: string, needed: string): boolean {
  if (userAuth === needed) return true
  // `application.*` é o gate coarse do painel admin: qualquer permissão granular
  // do catálogo (ex.: commercial.subscribers.view) satisfaz — espelha o backend
  // (CheckAuthority). Curingas de tipo não contam.
  if (needed === 'application.*' && !TYPE_WILDCARDS.includes(userAuth) && userAuth.includes('.')) {
    return true
  }
  if (userAuth.endsWith('.*')) {
    const prefix = userAuth.slice(0, -2)
    return needed.startsWith(prefix + '.') || needed === prefix
  }
  // needed é um curinga de módulo (ex.: `commercial.*`) e o usuário tem uma
  // permissão-folha do catálogo (ex.: `commercial.subscribers.view`) → cobre
  // (espelha o backend CheckAuthority::matches). É o caso da verificação "por módulo".
  if (needed.endsWith('.*')) {
    const prefix = needed.slice(0, -2)
    return userAuth.startsWith(prefix + '.') || userAuth === prefix
  }
  return false
}
